import APP_ASSETS from "@/assets";
import { Link } from "react-router-dom";
import DisputeButton from "@/components/DisputeButton";
import { useMerchant, useOrders } from "@/contexts";
import { formatAmount } from "@/utils/amount";
import { formatTimestamp } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { ethers } from "ethers";
import { OrderStatus, OrderType } from "@/models";
import _ from "lodash";
import { useEffect } from "react";
import { banks as bankSelector } from "../components/BankSelector";
import { useSelector } from "react-redux";
import { StateStatusEnum } from "@/enums/state-status.enum";
import { trackAmplitudeEvent } from "@/utils/amplitude";

type OrderCardType = {
  orderId: string;
  amount: string;
  completedAt: string;
  transationId: string;
  orderType: number;
  status: number;
  successCallback: () => void;
  redactedAccNum: number;
  merchantBank: string | null;
};

const OrderCard: React.FC<OrderCardType> = ({
  orderId,
  amount,
  completedAt,
  transationId,
  orderType,
  status,
  successCallback,
  redactedAccNum,
  merchantBank,
}) => {
  return (
    <div className="flex w-full flex-col gap-2 pb-3">
      <div className="flex w-full flex-col gap-2 rounded-md bg-bg-grey p-4">
        <div className="flex w-full items-center justify-between gap-2">
          <div className="flex gap-2">
            <img
              className="h-10 w-10"
              src={APP_ASSETS.TOKENS.INR_TRANSPARENT}
              alt="token"
            />
            <div className="flex flex-col">
              <div className="flex">
                <p className="font-satoshiBlack text-sm tracking-wide text-text-light">
                  {orderType === OrderType.SELL ? "Send" : "Receive"} INR
                </p>
                <div className="mx-2 flex items-center justify-center rounded-sm bg-yellow-500 px-1">
                  <p className="text-[10px] font-black tracking-widest text-black">
                    {OrderStatus[status]}
                  </p>
                </div>
              </div>
              <p className="font-satoshiBold text-sm tracking-wide text-gray-400">
                Order ID: {orderId}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <div className="flex flex-col items-end justify-between">
              <div className="flex gap-1">
                <p className="font-satoshiBold text-text-light">
                  {formatAmount(amount)}
                </p>
                <img src={APP_ASSETS.TOKENS.INR_TRANSPARENT} alt="INR" />
              </div>
              <p className="font-satoshiBold text-sm tracking-wide text-gray-400">
                {formatTimestamp(completedAt)}
              </p>
            </div>
          </div>
        </div>
        <div className="w-full">
          {orderType == OrderType.SELL ? (
            <DisputeButton
              orderId={orderId}
              label="Resolve Dispute"
              transId={transationId}
              onlyCta={false}
              orderType={orderType}
              successCallback={() => {
                trackAmplitudeEvent("DISPUTE_RESOLVED");
                successCallback();
              }}
              redactedAccNum={redactedAccNum}
              amount={parseFloat(formatAmount(amount).toString()).toString()}
              merchantBank={merchantBank}
            />
          ) : (
            <DisputeButton
              orderId={orderId}
              label="Resolve Dispute"
              transId={transationId}
              onlyCta
              orderType={orderType}
              successCallback={() => {
                trackAmplitudeEvent("DISPUTE_RESOLVED");
                successCallback();
              }}
              redactedAccNum={redactedAccNum}
              amount={parseFloat(formatAmount(amount).toString()).toString()}
              merchantBank={merchantBank}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const Disputes = () => {
  const { refreshOrders } = useOrders();
  const ordersData = useSelector((state: any) => state.orders.data);
  const isLoading = useSelector(
    (state: any) => state.orders.status == StateStatusEnum.LOADING,
  );
  const hasFetched = useSelector(
    (state: any) => state.orders.status == StateStatusEnum.SUCCESS,
  );
  const { merchantConfig, merchantBank } = useMerchant();
  useEffect(() => {
    refreshOrders();
  }, []);

  const loader = () => (
    <div className="flex w-full flex-col gap-2 py-4">
      {Array(6)
        .fill(1)
        .map((_, idx) => (
          <Skeleton key={idx} className="h-24 w-full" />
        ))}
    </div>
  );
  return (
    <main className="relative h-full max-h-[1200px] w-full max-w-[500px] bg-bg-tertiary">
      {/* ABSOLUTE POSITIONED TOPBAR */}
      <nav className="absolute top-0 flex h-14 w-full items-center justify-start gap-2 border-b border-primary bg-bg-tertiary p-4">
        <Link to={".."} relative="path">
          <img
            className="h-6 w-6"
            src={APP_ASSETS.ICONS.ARROW_LEFT}
            alt="img"
          />
        </Link>
        <p className="text-xl text-text-light">Disputes</p>
      </nav>
      {/* ABSOLUTE POSITIONED TOPBAR */}
      {/* DISPUTES */}
      <section className="no-scrollbar flex h-full flex-col items-start overflow-y-scroll px-4 pt-16">
        {/* HEADER */}
        <div className="flex flex-col items-start justify-center gap-2">
          <p className="text-2xl font-bold text-text-primary">
            Resolve disputes
          </p>
          {merchantBank && (
            <div className="flex">
              <img
                className="h-[26px]"
                src={bankSelector.find((_) => _.value == merchantBank)?.img}
              />
              <p className="ml-2 text-text-light">
                {bankSelector.find((_) => _.value == merchantBank)?.name}
              </p>
            </div>
          )}
          <p className="text-sm text-text-light">
            Resolve disputes by uploading the payment details. If you have any
            unresolved disputes, you will not get new orders. Contact admin in
            case you are not able to resolve disputes by yourself.
          </p>
        </div>
        {/* HEADER */}
        {/* RESOLVABLE LIST */}
        {isLoading || !hasFetched ? (
          loader()
        ) : (
          <div className="w-full pt-4 fade-in">
            {/* TODO FIX THIS before merge */}
            {ordersData?.allOrders
              .toJS()
              ?.filter((_: any) => _.disputeInfo?.status?.toString() == "1")
              ?.map((order: any, index: any) => (
                <OrderCard
                  key={index}
                  orderId={order.id}
                  amount={order.fiatAmount}
                  completedAt={order.completedTimestamp}
                  transationId={ethers?.utils?.formatUnits(
                    order.disputeInfo.redactTransId,
                    0,
                  )}
                  orderType={order.orderType}
                  status={order.status}
                  successCallback={() => {
                    refreshOrders();
                  }}
                  merchantBank={merchantBank}
                  redactedAccNum={
                    parseInt(
                      ethers?.utils?.formatUnits(
                        order.disputeInfo.accountNumber,
                        0,
                      ),
                    ) ??
                    merchantConfig?.redactedAccNum ??
                    0
                  }
                />
              ))}
          </div>
        )}
        {/* RESOLVABLE LIST */}
      </section>
      {/* DISPUTES */}
    </main>
  );
};

export default Disputes;
